import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentUploadStep = ({ formData, errors, onChange }) => {
  const cvRef = useRef(null);
  const tertiaryGraduationCertRef = useRef(null);
  const birthCertificateRef = useRef(null);
  const nyscAssignmentLetterRef = useRef(null);
  const meansOfIdRef = useRef(null);
  const passportPhotoRef = useRef(null);
  const recentPhotos1Ref = useRef(null);
  const recentPhotos2Ref = useRef(null);

  const handleFileUpload = (field, file) => {
    if (file) {
      // Validate file size (max 5MB)
      if (file?.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes?.includes(file?.type)) {
        alert('Only JPG, PNG, and PDF files are allowed');
        return;
      }

      // Create file preview
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange(field, {
          file,
          name: file?.name,
          size: file?.size,
          type: file?.type,
          preview: reader?.result
        });
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (field) => {
    onChange(field, null);
  };

  const DocumentUploadCard = ({ 
    title, 
    description, 
    field, 
    fileRef, 
    required = true,
    multiple = false 
  }) => {
    const fileData = formData?.[field];
    const hasError = errors?.[field];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`border-2 rounded-lg p-4 transition-all ${
          hasError 
            ? 'border-red-300 bg-red-50' 
            : fileData 
            ? 'border-green-300 bg-green-50' :'border-gray-200 bg-white hover:border-blue-300'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
              {required && <span className="text-red-500 text-xs">*</span>}
            </div>
            <p className="text-xs text-gray-600">{description}</p>
          </div>
          {fileData && (
            <Icon name="CheckCircle" size={20} color="#10B981" />
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => handleFileUpload(field, e?.target?.files?.[0])}
          className="hidden"
        />
        {fileData ? (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                  {fileData?.type?.includes('pdf') ? (
                    <Icon name="FileText" size={20} color="#3B82F6" />
                  ) : (
                    <Icon name="Image" size={20} color="#3B82F6" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileData?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(fileData?.size / 1024)?.toFixed(2)} KB
                  </p>
                </div>
              </div>
              <div className="flex gap-2 ml-2">
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Eye"
                  onClick={() => {
                    if (fileData?.preview) {
                      window.open(fileData?.preview, '_blank');
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Trash2"
                  onClick={() => handleRemoveFile(field)}
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            onClick={() => fileRef?.current?.click()}
            className="w-full"
          >
            Choose File
          </Button>
        )}
        {hasError && (
          <p className="text-xs text-red-600 mt-2">{errors?.[field]}</p>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Document Uploads
        </h2>
        <p className="text-sm text-gray-600">
          Upload all required documents. Accepted formats: JPG, PNG, PDF (Max 5MB each)
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <DocumentUploadCard
          title="Curriculum Vitae (CV)"
          description="Upload your current CV/Resume (PDF format preferred)"
          field="cv"
          fileRef={cvRef}
        />

        <DocumentUploadCard
          title="Tertiary Institution Graduation Certificate"
          description="Upload your university/polytechnic graduation certificate"
          field="tertiaryGraduationCert"
          fileRef={tertiaryGraduationCertRef}
        />

        <DocumentUploadCard
          title="Birth Certificate"
          description="Upload your official birth certificate"
          field="birthCertificate"
          fileRef={birthCertificateRef}
        />

        <DocumentUploadCard
          title="NYSC Assignment Letter"
          description="Upload your NYSC call-up/assignment letter"
          field="nyscAssignmentLetter"
          fileRef={nyscAssignmentLetterRef}
        />

        <DocumentUploadCard
          title="Means of Identification"
          description="Upload any valid government-issued ID (National ID, Driver's License, International Passport, Voter's Card)"
          field="meansOfId"
          fileRef={meansOfIdRef}
        />

        <DocumentUploadCard
          title="Passport Photograph"
          description="Upload a recent passport-sized photograph with white background"
          field="passportPhoto"
          fileRef={passportPhotoRef}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DocumentUploadCard
            title="Recent Photograph 1"
            description="Upload first recent photograph (full face, clear background)"
            field="recentPhoto1"
            fileRef={recentPhotos1Ref}
          />

          <DocumentUploadCard
            title="Recent Photograph 2"
            description="Upload second recent photograph (full face, clear background)"
            field="recentPhoto2"
            fileRef={recentPhotos2Ref}
          />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} color="#3B82F6" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Document Upload Guidelines
            </h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• All documents must be clear and legible</li>
              <li>• File size should not exceed 5MB per document</li>
              <li>• Accepted formats: JPG, PNG, PDF</li>
              <li>• Ensure all text in documents is readable</li>
              <li>• Photographs should have a plain/white background</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadStep;